import './vendor-detail-view.css';

import { Button } from '@kerfjs/ui/button';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { Row } from '@kerfjs/ui/row';
import { SegmentedControl } from '@kerfjs/ui/segmented-control';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import type { SafeHtml } from 'kerfjs';

import { EdgeToEdgeTable } from '../../components/edge-to-edge-table.js';
import type { VendorDetailT } from '../../data/api.js';
import { vendorDetailSection } from '../../data/state.js';

export function VendorDetailView({ detail: d }: { detail: VendorDetailT }): SafeHtml {
  return (
    <section class="vendor-detail-view" aria-labelledby="vendor-detail-title">
      <List gap="m" controlInsets="tb">
        <Row hAlign="center" controlInsets="rl">
          <SegmentedControl
            id="vendor-detail-section"
            label="Vendor Detail Sections"
            value={vendorDetailSection.value}
            action="vendor-detail-section"
            appearance="filled"
            shape="rounded"
            layout="equal"
            choices={[
              { value: 'details', label: 'Details' },
              { value: 'quotations', label: 'Quotation History' },
              { value: 'purchases', label: 'Purchases' },
            ]}
          />
        </Row>
        {vendorDetailSection.value === 'details' ? (
          <List controlInsets="rl">
            <ValueTable label="Vendor Details">
              {[
                <ValueTableRow label="Type" value={d.vendor.type ?? '—'} />,
                <ValueTableRow
                  label="Location"
                  value={[d.vendor.city, d.vendor.country].filter((x) => x !== null).join(', ') || '—'}
                />,
                <ValueTableRow label="Notes" value={d.vendor.notes ?? '—'} />,
              ]}
            </ValueTable>
          </List>
        ) : vendorDetailSection.value === 'quotations' ? (
          d.quotations.length === 0 ? (
            <EmptyState title="No Quotations from This Vendor" />
          ) : (
            <EdgeToEdgeTable label="Quotation History Table">
              <table class="t vendor-detail-grid">
                <thead>
                  <tr>
                    <th>Demand</th>
                    <th>Date</th>
                    <th>Currency</th>
                    <th>Basis</th>
                    <th class="n">Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {d.quotations.map((quote) => (
                    <tr data-key={quote.quotationId}>
                      <td>
                        <Button
                          emphasis="plain"
                          tone="brand"
                          size="compact"
                          rootAttributes={{ 'data-demand-id': quote.demandId }}
                        >
                          {quote.demandTitle}
                        </Button>
                      </td>
                      <td>{quote.quoteDate !== null ? quote.quoteDate.slice(0, 10) : '—'}</td>
                      <td>{quote.currency ?? '—'}</td>
                      <td>{quote.priceBasis ?? '—'}</td>
                      <td class="n">{String(quote.lineCount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </EdgeToEdgeTable>
          )
        ) : d.outcomes.length === 0 ? (
          <EmptyState title="No Purchases Recorded from This Vendor" />
        ) : (
          <EdgeToEdgeTable label="Purchases Table">
            <table class="t vendor-detail-grid">
              <thead>
                <tr>
                  <th>Demand</th>
                  <th>Item</th>
                  <th class="n">Final price</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {d.outcomes.map((outcome, index) => (
                  <tr data-key={String(index)}>
                    <td>{outcome.demandTitle}</td>
                    <td>{outcome.lineDescription}</td>
                    <td class="n">
                      {outcome.finalUnitPrice !== null ? `${outcome.finalUnitPrice} ${outcome.currency ?? ''}` : '—'}
                    </td>
                    <td>{outcome.outcomeDate !== null ? outcome.outcomeDate.slice(0, 10) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </EdgeToEdgeTable>
        )}
      </List>
    </section>
  );
}
